import React, { useEffect, useState } from "react";
import { Container, Row, Col, Image } from "react-bootstrap";
import FromPageIndicator from "../Common/FromPageIndicator";

const ScienceCommitteeMembers = () => {
  const [committeeData, setCommitteeData] = useState({
    scientificCommittee: [],
    coordinators: [],
    committeeMembers: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCommitteeData();
  }, []);

  const fetchCommitteeData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        "https://api.dremerz.net/api/DZ_8921_TnpyAmpicon_Create_Staff_List"
      );

      if (!response.ok) {
        throw new Error(`API request failed with status: ${response.status}`);
      }

      const data = await response.json();

      // Process and categorize committee members
      const scientificCommittee = data
        .filter(
          (member) => member.categoryName === "Scientific Committee Members"
        )
        .map(formatMemberData)
        .sort(
          (a, b) =>
            parseInt(a.Position || "999") - parseInt(b.Position || "999")
        );

      const coordinators = data
        .filter(
          (member) => member.categoryName === "Science Committee Coordinators"
        )
        .map(formatMemberData)
        .sort(
          (a, b) =>
            parseInt(a.Position || "999") - parseInt(b.Position || "999")
        );

      const committeeMembers = data
        .filter((member) => member.categoryName === "Science Committee Members")
        .map(formatMemberData)
        .sort(
          (a, b) =>
            parseInt(a.Position || "999") - parseInt(b.Position || "999")
        );

      setCommitteeData({
        scientificCommittee,
        coordinators,
        committeeMembers,
      });

      setLoading(false);
    } catch (error) {
      console.error("Error fetching committee data:", error);
      setError(error.message);
      setLoading(false);
    }
  };

  // Helper function to format member data
  const formatMemberData = (member) => {
    // Format the full designation with hospital and location
    let fullDesignation = member.designation || "";
    if (member.hospital)
      fullDesignation += member.hospital ? ` ${member.hospital}` : "";
    if (member.location)
      fullDesignation += member.location ? ` ${member.location}` : "";

    return {
      ...member,
      fullDesignation:
        fullDesignation || member.designation || member.location || "",
    };
  };

  // Render committee section
  const renderCommitteeSection = (title, members) => {
    if (!members || members.length === 0) return null;

    return (
      <>
        <h4 className="text-center mb-4">{title}</h4>
        <Row className="justify-content-center mb-5">
          {members.map((member) => (
            <Col
              key={member.id}
              xs={12}
              sm={6}
              md={4}
              lg={3}
              className="d-flex flex-column align-items-center mb-4"
            >
              <Image
                src={`https://api.dremerz.net/uploads/${member.image}`}
                roundedCircle
                alt={member.name}
                width={150}
                height={150}
                className="border border-4 border-danger-subtle shadow-sm"
                onError={(e) => {
                  e.target.src = "/assets/avatar.jpg";
                }}
              />
              <p className="mt-3 mb-1 fw-semibold text-center">{member.Role}</p>
              <p className="text-danger fw-bold mb-1 text-center">
                {member.name}
              </p>
              <p className="mb-0 text-muted small text-center">
                {member.fullDesignation}
              </p>
            </Col>
          ))}
        </Row>
      </>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="py-5 text-center">
        <Container>
          <h3>Loading Scientific Committee data...</h3>
        </Container>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="py-5 text-center">
        <Container>
          <h3 className="text-danger">Error loading committee data</h3>
          <p>{error}</p>
          <button
            className="btn btn-primary mt-3"
            onClick={() => fetchCommitteeData()}
          >
            Try Again
          </button>
        </Container>
      </div>
    );
  }

  // Empty state
  const hasNoData =
    committeeData.scientificCommittee.length === 0 &&
    committeeData.coordinators.length === 0 &&
    committeeData.committeeMembers.length === 0;

  if (hasNoData) {
    return (
      <>
        <FromPageIndicator />
        <section className="py-5" style={{ backgroundColor: "#FFF4FA" }}>
          <Container>
            <h3 className="mb-4 fw-bold text-center">
              Scientific Committee Members
            </h3>
            <p className="text-center">No committee members found.</p>
          </Container>
        </section>
      </>
    );
  }

  return (
    <>
      <FromPageIndicator />
      <section className="py-5" style={{ backgroundColor: "#FFF4FA" }}>
        <Container>
          <h3 className="mb-4 fw-bold text-center">
            Scientific Committee Members
          </h3>

          {/* Display in the requested order: Scientific Committee first, 
              then Coordinators, then Committee Members */}
          {renderCommitteeSection("", committeeData.scientificCommittee)}
          {renderCommitteeSection(
            "Science Committee Coordinators",
            committeeData.coordinators
          )}
          {renderCommitteeSection(
            "Science Committee Members",
            committeeData.committeeMembers
          )}
        </Container>
      </section>
    </>
  );
};

export default ScienceCommitteeMembers;
